import { useEffect, useState } from "react";
import { Plus, Copy, ExternalLink, Trash2, Link2, Loader2, Power } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, EmptyState } from "@/components/dash";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api, { apiErr } from "@/lib/api";
import { formatTRY } from "@/lib/format";

const FRONT = process.env.REACT_APP_BACKEND_URL;

export default function PaymentLinks() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ product_name: "", image_url: "", amount_try: "", description: "", usage_limit: 0, expires_at: "" });

  const load = () => api.get("/payment-links").then(({ data }) => setItems(data.links)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/payment-links", { ...form, amount_try: parseFloat(form.amount_try), usage_limit: parseInt(form.usage_limit) || 0 });
      toast.success("Ödeme linki oluşturuldu.");
      setOpen(false);
      setForm({ product_name: "", image_url: "", amount_try: "", description: "", usage_limit: 0, expires_at: "" });
      load();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };

  const checkoutFor = async (id) => {
    try {
      const { data } = await api.post(`/payment-links/${id}/checkout`);
      window.open(`${FRONT}${data.pay_url}`, "_blank");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const copy = (id) => { navigator.clipboard.writeText(`${FRONT}/l/${id}`); toast.success("Link kopyalandı."); };
  const toggle = async (l) => { await api.patch(`/payment-links/${l.id}`, { active: !l.active }); load(); };
  const del = async (id) => { await api.delete(`/payment-links/${id}`); toast.success("Silindi."); load(); };

  return (
    <div>
      <PageHeader title="Ödeme Linkleri" desc="Ürün bazlı paylaşılabilir ödeme linkleri oluşturun."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="create-link-btn" className="rounded-full"><Plus size={16} /> Link Oluştur</Button></DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Yeni Ödeme Linki</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3.5" data-testid="create-link-form">
                <div><Label className="text-sm">Ürün Adı</Label><Input required value={form.product_name} onChange={set("product_name")} data-testid="link-name" className="mt-1.5" placeholder="Premium Paket" /></div>
                <div><Label className="text-sm">Ürün Görseli (URL)</Label><Input value={form.image_url} onChange={set("image_url")} className="mt-1.5" placeholder="https://..." /></div>
                <div><Label className="text-sm">TL Fiyatı</Label><Input required type="number" step="0.01" value={form.amount_try} onChange={set("amount_try")} data-testid="link-amount" className="mt-1.5" placeholder="500.00" /></div>
                <div><Label className="text-sm">Açıklama</Label><Textarea value={form.description} onChange={set("description")} className="mt-1.5 min-h-16" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-sm">Kullanım Limiti (0=sınırsız)</Label><Input type="number" value={form.usage_limit} onChange={set("usage_limit")} className="mt-1.5" /></div>
                  <div><Label className="text-sm">Son Kullanma</Label><Input type="date" value={form.expires_at} onChange={set("expires_at")} className="mt-1.5" /></div>
                </div>
                <Button type="submit" disabled={saving} data-testid="link-submit" className="w-full rounded-full h-11">{saving ? <Loader2 className="animate-spin" size={16} /> : "Oluştur"}</Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      {items === null ? <Spinner /> : items.length === 0 ? (
        <EmptyState icon={Link2} title="Henüz ödeme linki yok" desc="İlk ödeme linkinizi oluşturun ve paylaşın." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="links-grid">
          {items.map((l) => (
            <Card key={l.id} className="p-5">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="font-bold truncate">{l.product_name}</p>
                  <p className="text-lg font-extrabold tracking-tighter text-primary mt-1">{formatTRY(l.amount_try)}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${l.active ? "bg-emerald-500/10 text-emerald-500" : "bg-muted text-muted-foreground"}`}>{l.active ? "Aktif" : "Pasif"}</span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Kullanım: {l.used}{l.usage_limit ? ` / ${l.usage_limit}` : ""}</p>
              <div className="mt-4 flex gap-1.5">
                <Button size="sm" variant="outline" className="flex-1 rounded-lg" onClick={() => checkoutFor(l.id)}><ExternalLink size={14} /> Aç</Button>
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => copy(l.id)}><Copy size={14} /></Button>
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => toggle(l)}><Power size={14} /></Button>
                <Button size="sm" variant="outline" className="rounded-lg text-red-500" onClick={() => del(l.id)}><Trash2 size={14} /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
